import { useFetchMgItemCard, useMutateMgSupplierItemsSearch } from '@retail/products/data-access';
import {
  MgItemCard,
  MgSupplierItemSearchResult,
  MgSupplierItemSearchResultDTO,
} from '@retail/products/types';
import { useDebounce } from '@retail/hooks';
import { useEffect, useMemo } from 'react';
import { PaginationResultDTO } from '@shared/fetch-utils';

interface Props {
  supplierId?: number;
  selectedMgItemNumber?: number;
  itemSearch?: string;
  onItemSearchSuccess?: (result: PaginationResultDTO<MgSupplierItemSearchResultDTO[]>) => void;
  onItemSearchError?: (error: any) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const useItemSearch = ({
  supplierId,
  selectedMgItemNumber,
  itemSearch = '',
  onItemSearchSuccess = noop,
  onItemSearchError = noop,
}: Props) => {
  const {
    data: itemsDto,
    isLoading: isLoadingFetchItems,
    mutateAsync: searchMgSupplierItems,
    reset: resetSearch,
  } = useMutateMgSupplierItemsSearch();

  const query = useDebounce(itemSearch, 400);

  useEffect(() => {
    if (!query) {
      resetSearch();
      return;
    }

    searchMgSupplierItems({
      urlVariables: {
        supplierIds: supplierId ? [supplierId] : [],
        size: 80,
        page: 0,
        query,
      },
    })
      .then(onItemSearchSuccess)
      .catch(onItemSearchError);
    // eslint-disable-next-line
  }, [query]);

  const {
    data: mgItemDTO,
    isLoading: isLoadingMgItem,
    error: errorLoadingMgItem,
  } = useFetchMgItemCard(
    {
      mgItemNumber: selectedMgItemNumber || 0,
    },
    { enabled: !!selectedMgItemNumber, suspense: false }
  );
  const mgItem = useMemo(
    () => (mgItemDTO ? MgItemCard.fromDto(mgItemDTO) : undefined),
    [mgItemDTO]
  );

  const mgSupplierItems = useMemo(
    () =>
      itemsDto?.result
        ? MgSupplierItemSearchResult.fromSearchResultDTOs(itemsDto.result, [])
        : undefined,
    [itemsDto?.result]
  );

  return {
    mgSupplierItems,
    isLoadingFetchItems,
    isLoadingMgItem,
    errorLoadingMgItem,
    mgItem,
  };
};
